import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Chip,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { INDIAN_CITIES, getNearbyCities } from '../utils/indianCities';

/**
 * SearchableCityDropdown Component
 * MUI Autocomplete with grouped cities (Your City, Nearby Cities, All Cities)
 */
function SearchableCityDropdown({
  value,
  onChange,
  detectedCity,
  includeNearby,
  onIncludeNearbyChange,
  showIncludeNearbyCheckbox,
  label,
  placeholder,
  size,
  fullWidth,
  disabled,
  error,
  helperText,
  multiple,
}) {
  // Get nearby cities for detected city
  const nearbyCitiesList = useMemo(() => {
    if (!detectedCity) return [];
    return getNearbyCities(detectedCity);
  }, [detectedCity]);

  // Group cities into categories
  const groupedOptions = useMemo(() => {
    const grouped = [];

    // Add detected city first if available
    if (detectedCity && INDIAN_CITIES.includes(detectedCity)) {
      grouped.push({ city: detectedCity, group: 'Your City' });
    }

    // Add nearby cities
    nearbyCitiesList.forEach((city) => {
      if (INDIAN_CITIES.includes(city)) {
        grouped.push({ city, group: 'Nearby Cities' });
      }
    });

    // Add all other cities
    INDIAN_CITIES.forEach((city) => {
      if (city !== detectedCity && !nearbyCitiesList.includes(city)) {
        grouped.push({ city, group: 'All Cities' });
      }
    });

    return grouped;
  }, [detectedCity, nearbyCitiesList]);

  // For single select, convert string value to option object
  const selectedValue = useMemo(() => {
    if (multiple) {
      if (!value || value.length === 0) return [];
      return value.map((v) => {
        const found = groupedOptions.find((opt) => opt.city === v);
        return found || { city: v, group: 'All Cities' };
      });
    }
    if (!value) return null;
    const found = groupedOptions.find((opt) => opt.city === value);
    return found || { city: value, group: 'All Cities' };
  }, [value, groupedOptions, multiple]);

  const handleChange = (event, newValue) => {
    if (multiple) {
      onChange(newValue ? newValue.map((v) => v.city) : []);
    } else {
      onChange(newValue ? newValue.city : null);
    }
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Autocomplete
        value={selectedValue}
        onChange={handleChange}
        options={groupedOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.city || ''}
        isOptionEqualToValue={(option, val) => option.city === val.city}
        size={size}
        disabled={disabled}
        multiple={multiple}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {!multiple && selectedValue && (
                    <LocationOnIcon
                      sx={{ color: 'action.active', mr: 1, ml: 0.5 }}
                      fontSize="small"
                    />
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.city}>
            {option.group === 'Your City' && (
              <LocationOnIcon
                sx={{ color: 'primary.main', mr: 1 }}
                fontSize="small"
              />
            )}
            {option.city}
          </Box>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.city}
              label={option.city}
              size="small"
              icon={
                option.group === 'Your City' ? (
                  <LocationOnIcon fontSize="small" />
                ) : undefined
              }
            />
          ))
        }
        sx={{ width: fullWidth ? '100%' : 250 }}
      />

      {showIncludeNearbyCheckbox && !multiple && (
        <FormControlLabel
          control={
            <Checkbox
              checked={includeNearby}
              onChange={(e) => onIncludeNearbyChange(e.target.checked)}
              size="small"
            />
          }
          label="Include nearby cities"
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

SearchableCityDropdown.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func.isRequired,
  detectedCity: PropTypes.string,
  includeNearby: PropTypes.bool,
  onIncludeNearbyChange: PropTypes.func,
  showIncludeNearbyCheckbox: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  multiple: PropTypes.bool,
};

SearchableCityDropdown.defaultProps = {
  value: null,
  detectedCity: null,
  includeNearby: true,
  onIncludeNearbyChange: () => {},
  showIncludeNearbyCheckbox: false,
  label: 'City',
  placeholder: 'Search city...',
  size: 'medium',
  fullWidth: false,
  disabled: false,
  error: false,
  helperText: '',
  multiple: false,
};

export default SearchableCityDropdown;
